
import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchNotifications } from "../../features/admin/PostsSlice";
import TableComponent from "../../components/TableComponent";
import ApiCustom from "../../utils/ApiCustom";
import QRCode from "react-qr-code";

import {
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  XCircleIcon,
  InformationCircleIcon,
  ShareIcon,
  ClipboardDocumentIcon,
  LinkIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { deletePost } from "../../features/admin/PostsSlice";
import { toast } from "react-toastify";
import {
  fetchCategoryByType,
  fetchCategoryByTypePublic,
} from "../../features/admin/CategorySlice";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import { fetchPartners } from "../../features/admin/PartnerSlice";
import NotiModal from "../../components/admin/NotiModal";
import NotiPreview from "../../components/admin/NotiPreview";
import { fetchRegistration } from "../../features/admin/RegistrationSlice";
import EventModal from "../../components/admin/eventModal";
import ConfirmCancelApproveModal from "../../components/ConfirmCancelApproveModal";
import ConfirmApproveModal from "../../components/ConfirmApproveModal";
import { formatDate } from "../../utils/helps";
import { renderTextOrUnknown } from "../../utils/helpers";

const Posts = () => {
  const dispatch = useDispatch();
  const {
    authNotifications: posts,
    authNotificationStatus: status,
    authNotificationError: error,
  } = useSelector((state) => state.posts);
  const { registrations } = useSelector((state) => state.registrations);
  const logo = useSelector((state) => state.logo.logo);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOpenEvent, setIsOpenEvent] = useState(false);
  const [isModalPreview, setIsModalPreview] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [eventPostId, setEventPostId] = useState(null);
  const [isCancelApproveOpen, setIsCancelApproveOpen] = useState(false);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [sharePost, setSharePost] = useState(null);
  const shareQrRef = useRef(null);

  const hasPermission = (requiredPermission) => {
    const userId = localStorage.getItem("userId");
    if (Number(userId) === 1) {
      return true;
    }
    const userPermissions = getUserPermissions();
    return userPermissions.includes(requiredPermission);
  };

  const getUserPermissions = () => {
    const permissions = localStorage.getItem("permissions");
    return permissions
      ? JSON.parse(permissions)?.map((p) => p.permissionSlug)
      : [];
  };

  const [statusFilter, setStatusFilter] = useState("all");

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const handleOpenShareModal = (post) => {
    setSharePost(post);
    setIsShareOpen(true);
  };

  const handleCloseShareModal = () => {
    setSharePost(null);
    setIsShareOpen(false);
  };

  const getShareLink = (post) => {
    if (!post?.slug || !post?.category?.slug || !post?.guestRegisCode) return "";
    return `${window.location.origin}/thong-bao/${post.category.slug}/${post.slug}?code=${post.guestRegisCode}`;
  };

  const handleCopyShareLink = async () => {
    const link = getShareLink(sharePost);
    if (!link) {
      toast.error("Không tìm thấy link chia sẻ");
      return;
    }

    try {
      await navigator.clipboard.writeText(link);
      toast.success("Đã sao chép link chia sẻ");
    } catch (error) {
      toast.error("Không thể sao chép link");
    }
  };

  const handleDownloadShareQr = () => {
    const link = getShareLink(sharePost);
    const svg = shareQrRef.current?.querySelector("svg");

    if (!link || !svg) {
      toast.error("Không tìm thấy mã QR");
      return;
    }

    const clonedSvg = svg.cloneNode(true);

    if (!clonedSvg.getAttribute("xmlns")) {
      clonedSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    }

    const svgData = new XMLSerializer().serializeToString(clonedSvg);
    const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      svgData,
    )}`;
    const qrImage = new Image();

    qrImage.onload = () => {
      const canvas = document.createElement("canvas");
      const canvasSize = 1024;
      const qrPadding = 64;
      const qrSize = canvasSize - qrPadding * 2;
      const ctx = canvas.getContext("2d");

      canvas.width = canvasSize;
      canvas.height = canvasSize;

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvasSize, canvasSize);
      ctx.drawImage(qrImage, qrPadding, qrPadding, qrSize, qrSize);

      const downloadCanvas = () => {
        canvas.toBlob((blob) => {
          if (!blob) {
            toast.error("Không thể tạo file QR");
            return;
          }

          const objectUrl = URL.createObjectURL(blob);
          const downloadLink = document.createElement("a");
          const fileName = String(
            sharePost?.slug || sharePost?.id || "khach-vang-lai",
          )
            .trim()
            .replace(/[^a-zA-Z0-9-_]+/g, "-")
            .replace(/^-+|-+$/g, "");

          downloadLink.href = objectUrl;
          downloadLink.download = `qr-dang-ky-${fileName || "khach-vang-lai"}.png`;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(objectUrl);
        }, "image/png");
      };

      if (!logo) {
        downloadCanvas();
        return;
      }

      const logoImage = new Image();

      logoImage.crossOrigin = "anonymous";
      logoImage.onload = () => {
        const logoBoxSize = 184;
        const logoPadding = 24;
        const logoBoxX = canvasSize / 2 - logoBoxSize / 2;
        const logoBoxY = canvasSize / 2 - logoBoxSize / 2;
        const radius = 28;

        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.moveTo(logoBoxX + radius, logoBoxY);
        ctx.lineTo(logoBoxX + logoBoxSize - radius, logoBoxY);
        ctx.quadraticCurveTo(
          logoBoxX + logoBoxSize,
          logoBoxY,
          logoBoxX + logoBoxSize,
          logoBoxY + radius,
        );
        ctx.lineTo(
          logoBoxX + logoBoxSize,
          logoBoxY + logoBoxSize - radius,
        );
        ctx.quadraticCurveTo(
          logoBoxX + logoBoxSize,
          logoBoxY + logoBoxSize,
          logoBoxX + logoBoxSize - radius,
          logoBoxY + logoBoxSize,
        );
        ctx.lineTo(logoBoxX + radius, logoBoxY + logoBoxSize);
        ctx.quadraticCurveTo(
          logoBoxX,
          logoBoxY + logoBoxSize,
          logoBoxX,
          logoBoxY + logoBoxSize - radius,
        );
        ctx.lineTo(logoBoxX, logoBoxY + radius);
        ctx.quadraticCurveTo(
          logoBoxX,
          logoBoxY,
          logoBoxX + radius,
          logoBoxY,
        );
        ctx.closePath();
        ctx.fill();

        const maxLogoSize = logoBoxSize - logoPadding * 2;
        const scale = Math.min(
          maxLogoSize / logoImage.naturalWidth,
          maxLogoSize / logoImage.naturalHeight,
        );
        const logoWidth = logoImage.naturalWidth * scale;
        const logoHeight = logoImage.naturalHeight * scale;

        ctx.drawImage(
          logoImage,
          canvasSize / 2 - logoWidth / 2,
          canvasSize / 2 - logoHeight / 2,
          logoWidth,
          logoHeight,
        );

        downloadCanvas();
      };
      logoImage.onerror = downloadCanvas;
      logoImage.src = logo;
    };

    qrImage.onerror = () => {
      toast.error("Không thể tạo mã QR");
    };

    qrImage.src = svgUrl;
  };

  const handleOpenCancelApproveModal = (post) => {
    setCurrentPost(post);
    setIsCancelApproveOpen(true);
  };

  const handleCloseCancelApproveModal = () => {
    setIsCancelApproveOpen(false);
    setCurrentPost(null);
    dispatch(fetchNotifications());
  };

  const handleCloseApproveModal = () => {
    setIsApproveOpen(false);
    setCurrentPost(null);
    dispatch(fetchNotifications());
  };

  const handleCloseDeleteModal = () => {
    setDeleteId(null);
    setIsDeleteOpen(false);
  };

  const handleOpenModal = (post = null) => {
    setCurrentPost(post);
    setIsModalOpen(true);
  };

  const handleOpenEvent = (id) => {
    ApiCustom.get("/event-show-by-post-id/" + id)
      .then((res) => {
        if (res.data.data.length > 0) {
          setCurrentEvent(res.data.data[0]);
        } else {
          setEventPostId(id);
        }
        setIsOpenEvent(true);
      })
      .catch((err) => console.log(err));
  };

  const handleOpenApproveModal = (post = null) => {
    setCurrentPost(post);
    setIsApproveOpen(true);
  };

  const handleApprove = () => {
    ApiCustom.put("/notification-approve/" + currentPost?.id, {
      status: "published",
    })
      .then((res) => {
        if (Number(res.status) === 200) {
          setTimeout(() => {
            toast.success("duyệt thông báo thành công!");
          }, 300);

          handleCloseApproveModal();
          handleCloseCancelApproveModal();
        }
      })
      .catch((err) => console.log(err));
  };

  const handleCancelApprove = () => {
    ApiCustom.put("/notification-approve/" + currentPost?.id, {
      status: "draft",
    })
      .then((res) => {
        if (Number(res.status) === 200) {
          setTimeout(() => {
            toast.success("Đã huỷ duyệt thông báo");
          }, 300);

          handleCloseApproveModal();
          handleCloseCancelApproveModal();
        }
      })
      .catch((err) => console.log(err));
  };

  const handleCloseModalPreview = () => {
    setIsModalPreview(false);
    setCurrentPost(null);
    dispatch(fetchNotifications());
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentPost(null);
  };

  const handleCloseEvent = () => {
    setIsOpenEvent(false);
    setCurrentEvent(null);
    setEventPostId(null);
  };

  const handleDelete = async () => {
    await dispatch(deletePost(deleteId)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        handleCloseDeleteModal();
        toast.success("Xóa bài viết thành công!");
      } else {
        if (Number(res.payload.status) === 400) {
          toast.error(res.payload.messages);
          return;
        }
        toast.error("Đã xảy ra lỗi khi xóa bài viết!");
      }
    });
  };

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchNotifications());
      dispatch(fetchPartners);
    }
  }, [status, dispatch]);

  const userId = localStorage.getItem("userId");
  useEffect(() => {
    window.scrollTo(0, 0);
    if (Number(userId) === 1) {
      dispatch(fetchCategoryByTypePublic("notification"));
    } else {
      dispatch(fetchCategoryByType("notification"));
    }
    dispatch(fetchRegistration());
  }, [dispatch, userId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (status === "loading") {
    return <p>Loading...</p>;
  }

  if (status === "failed") {
    return <p>Error: {error}</p>;
  }

  const filteredPosts =
    statusFilter === "all"
      ? posts
      : posts
          ?.filter((e) => e.type === "notification")
          ?.filter((post) => post.status === statusFilter);
  const headers = [
    { key: "title", label: "Tiêu đề" },
    {
      key: "category",
      label: "Danh mục",
      render: (item) => <div>{item.category?.name}</div>,
    },
    {
      key: "approvedAt",
      label: "Ngày duyệt",
      render: (item) =>
        renderTextOrUnknown(item?.approvedAt && formatDate(item.approvedAt)),
    },
    {
      key: "approvedBy",
      label: "Người duyệt",
      render: (item) => renderTextOrUnknown(item?.approvedByUser?.userFullName),
    },
    {
      key: "status",
      label: "Trạng thái",
      render: (item) => (
        <span
          className={`px-3 py-1 rounded-full text-sm ${
            item.status === "published"
              ? "bg-green-100 text-green-800"
              : "bg-yellow-100 text-yellow-800"
          } whitespace-nowrap`}
        >
          {item.status === "published" ? "Đã duyệt" : "Chờ duyệt"}
        </span>
      ),
    },
  ];

  if (
    hasPermission("POST_UPDATE") ||
    hasPermission("POST_DELETE") ||
    hasPermission("EVENT_SHOWS") ||
    hasPermission("NOTIFICATION_APPROVE")
  ) {
    headers.push({ key: "setting", label: "Phương thức" });
  }

  const tableSettings = {
    renderSettings: (item) => (
      <div className="flex space-x-2">
        {hasPermission("NOTIFICATION_APPROVE") && (
          <button
            onClick={() =>
              String(item.status) === "draft"
                ? handleOpenApproveModal(item)
                : handleOpenCancelApproveModal(item)
            }
            className={`btn btn-outline ${
              String(item.status) === "published" ? "btn-error" : "btn-success"
            } btn-sm px-3 my-1 rounded`}
            title={String(item.status) === "published" ? "Huỷ duyệt" : "Duyệt"}
          >
            {String(item.status) === "published" ? (
              <XCircleIcon className="w-5 h-5" />
            ) : (
              <CheckCircleIcon className="w-5 h-5" />
            )}
          </button>
        )}
        {hasPermission("POST_UPDATE") && (
          <button
            onClick={() => handleOpenModal(item)}
            // disabled={item.status == "published"}
            className={`btn btn-outline ${
              String(item.status) === "published"
                ? "btn-primary"
                : "btn-warning"
            } btn-sm px-3 my-1 rounded`}
            title={
              String(item.status) === "published" ? "Chi tiết" : "Chỉnh sửa"
            }
          >
            {String(item.status) === "published" ? (
              <InformationCircleIcon className="w-5 h-5" />
            ) : (
              <PencilSquareIcon className="w-5 h-5" />
            )}
          </button>
        )}
        {hasPermission("EVENT_SHOWS") && (
          <button
            onClick={() => handleOpenEvent(item.id)}
            className="btn btn-outline btn-success btn-sm px-3 my-1 rounded "
            title="Thiệp mời sự kiện"
          >
            <DocumentTextIcon className="w-5 h-5" />
          </button>
        )}
        <button
          onClick={() => handleOpenShareModal(item)}
          className="btn btn-outline btn-info btn-sm px-3 my-1 rounded"
          title="Chia sẻ đăng ký"
        >
          <ShareIcon className="w-5 h-5" />
        </button>
        {hasPermission("POST_DELETE") && (
          <button
            onClick={() => handleOpenDeleteModal(item.id)}
            className=" btn btn-outline btn-error btn-sm px-3 my-1 rounded"
            disabled={String(item.status) === "published"}
            title="Xóa"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    ),
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Quản lý hoạt động</h1>
      <div className="block md:flex justify-between items-center mb-1">
        {hasPermission("POST_CREATE") ? (
          <button
            onClick={() => handleOpenModal()}
            className="btn btn-success transition-colors px-4 py-2 mb-4 rounded"
          >
            + Thêm hoạt động
          </button>
        ) : (
          <div></div>
        )}
        <div className="flex items-center space-x-2">
          <label htmlFor="userTypeFilter" className="text-sm font-medium">
            Trạng thái:
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="select select-bordered select-sm"
          >
            <option value="all">Tất cả</option>
            <option value="published">Đã duyệt</option>
            <option value="draft">Chờ duyệt</option>
          </select>
        </div>
      </div>

      <div className="rounded-lg shadow">
        <TableComponent
          headers={headers}
          data={filteredPosts}
          settings={tableSettings}
        />
      </div>

      {isModalOpen && (
        <NotiModal
          postId={currentPost?.id || null}
          onClose={handleCloseModal}
          canUpdate={
            currentPost != null ? currentPost.status !== "published" : true
          }
        />
      )}

      {isModalPreview && (
        <NotiPreview
          post={currentPost}
          onClose={handleCloseModalPreview}
          registrations={registrations}
        />
      )}
      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDelete}
      />

      <EventModal
        isOpen={isOpenEvent}
        data={currentEvent}
        eventPostId={eventPostId}
        onClose={handleCloseEvent}
      />
      <ConfirmApproveModal
        isOpen={isApproveOpen}
        onClose={handleCloseApproveModal}
        onConfirm={handleApprove}
      />
      <ConfirmCancelApproveModal
        isOpen={isCancelApproveOpen}
        onClose={handleCloseCancelApproveModal}
        onConfirm={handleCancelApprove}
      />

      {isShareOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 py-6">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[calc(100vh-3rem)] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 px-6 py-5 border-b">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                    <ShareIcon className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-gray-800">
                      Chia sẻ đăng ký khách vãng lai
                    </h2>
                    <p className="text-sm text-gray-500 mt-1 break-words">
                      {sharePost?.title}
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleCloseShareModal}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:bg-white hover:text-gray-700 transition shrink-0"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6">
              {getShareLink(sharePost) ? (
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_246px] gap-6 items-start">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <LinkIcon className="w-5 h-5 text-blue-500" />
                      Link đăng ký
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch gap-2">
                      <input
                        type="text"
                        readOnly
                        value={getShareLink(sharePost)}
                        className="input input-bordered w-full min-w-0 text-sm bg-gray-50"
                      />
                      <button
                        onClick={handleCopyShareLink}
                        className="btn btn-info text-white px-4 rounded shrink-0"
                        title="Sao chép"
                      >
                        <ClipboardDocumentIcon className="w-5 h-5" />
                        Copy
                      </button>
                    </div>

                    <div className="flex items-start gap-2 mt-4 rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm text-yellow-700">
                      <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
                      <span>
                        Chỉ gửi link này cho nhóm khách được mời. Người có link có
                        thể mở trang hoạt động và đăng ký tham gia.
                      </span>
                    </div>
                  </div>

                  <div className="w-full max-w-[246px] mx-auto rounded-2xl border border-gray-200 bg-gray-50 p-3 shadow-sm">
                    <div className="text-center text-sm font-semibold text-gray-600 mb-3">
                      QR đăng ký khách
                    </div>

                    <div
                      ref={shareQrRef}
                      className="relative mx-auto flex h-[220px] w-[220px] items-center justify-center overflow-hidden rounded-xl bg-white"
                    >
                      <QRCode
                        value={getShareLink(sharePost)}
                        size={220}
                        level="H"
                      />

                      {logo && (
                        <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-xl bg-white p-1 shadow-sm">
                          <img
                            src={logo}
                            alt="Logo"
                            className="h-12 w-12 rounded-lg object-contain"
                          />
                        </div>
                      )}
                    </div>

                    <button
                      onClick={handleDownloadShareQr}
                      className="btn btn-outline btn-success btn-sm w-full mt-4 rounded"
                    >
                      <ArrowDownTrayIcon className="w-5 h-5" />
                      Tải QR
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-3 text-sm text-red-600">
                  <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
                  <span>Hoạt động này chưa có mã chia sẻ.</span>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={handleCloseShareModal}
                  className="btn btn-sm rounded px-5"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Posts;
